import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe, Sparkles, ShieldCheck, Check, Coins, MessageCircleMore, Megaphone, Clapperboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { PlanCard, CUSTOM_PLAN } from "@/components/nadia/PlanCard";
import { getBillingConfig } from "@/lib/api";

export default function Home() {
  const [url, setUrl] = useState("");
  const [cfg, setCfg] = useState(null);
  const nav = useNavigate();
  useEffect(() => { getBillingConfig().then(setCfg).catch(() => {}); }, []);

  const go = () => {
    const u = url.trim();
    if (!u) return toast.error("Inserisci l'URL del sito o del profilo social della tua attività");
    if (!/^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/.*)?$/i.test(u)) return toast.error("Indirizzo non valido. Esempio: https://www.tuaazienda.it oppure instagram.com/tuaattivita");
    sessionStorage.setItem("nadia_demo_url", u);
    nav(`/demo?url=${encodeURIComponent(u)}`);
  };

  return (
    <div className="min-h-screen">
      <style data-nadia-home-theme>{`
        div.min-h-screen:has(> style[data-nadia-home-theme]) {
          color: #211833;
          background: radial-gradient(ellipse at top right, #eee5ff, transparent 55%), #f8f5ff;

          & .nadia-card { background: #fff; border: 1px solid #e6dcf5; border-radius: 20px; box-shadow: 0 8px 24px #482b7510; }
          & :is(.text-slate-50, .text-slate-100, .text-slate-200) { color: #211833; }
          & :is(.text-slate-300, .text-slate-400, .text-slate-500) { color: #594d6c; }
          & [class*="text-cyan-"] { color: #6429bd; }
          & [class*="bg-cyan-"] { background-color: #f1e8ff; }
          & :is([class*="border-white/"], [class*="border-cyan-"]) { border-color: #e4d9f4; }
          & [class*="bg-white/"] { background-color: #f8f3ff; }
          & .text-ai-gradient { background-image: linear-gradient(100deg, #5824b5, #8546dc); }
          & [class*="text-emerald-"] { color: #10644f; }
          & input { color: #211833; background: #fff; border-color: #d9c8ed; border-radius: 12px; }
          & input::placeholder { color: #655675; }
          & :is(button, a)[class*="from-cyan-"] { background: #6327be; color: #fff; }
          & :is(button, a)[class*="from-cyan-"]:hover { background: #51209e; }
          & :is(a, button, input):focus-visible { outline: 2px solid #7237cc; outline-offset: 3px; }
        }
      `}</style>
      <header className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <NadiaAvatar size="md" />
          <div>
            <div className="font-heading text-sm font-bold text-slate-50 leading-tight">Nadia Cloud</div>
            <div className="text-[11px] text-slate-400">Direttore Marketing AI</div>
          </div>
        </div>
        <nav className="flex items-center gap-4 text-sm">
          <Link to="/piani" className="text-slate-300 hover:text-cyan-200" data-testid="home-nav-piani">Piani</Link>
          <Link to="/accedi" className="text-slate-300 hover:text-cyan-200" data-testid="home-nav-app">Accedi</Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-10 lg:pt-20 pb-16 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
        <div className="animate-fade-in-up">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300/80">Nadia Cloud · Il Direttore Marketing AI per la tua impresa</div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="home-hero-title">
            Un unico URL, <span className="text-ai-gradient">tutto il resto lo faccio io.</span>
          </h1>
          <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-lg sm:text-xl font-bold text-emerald-200" data-testid="home-unlimited-posts"><Check size={22} className="shrink-0" aria-hidden="true" />Pubblica post illimitati</div>
          <p className="mt-5 text-slate-400 text-base md:text-lg max-w-xl">
            Inserisci il sito web oppure il link al profilo Instagram, Facebook o TikTok della tua attività. Nadia capisce cosa fai e ti mostra come può aiutarti a ottenere più clienti.
          </p>
          <div className="mt-8 nadia-card p-4 sm:p-5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Inserisci l'URL del sito o del profilo social della tua attività</label>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} placeholder="https://www.tuaazienda.it · instagram.com/tuaattivita · facebook.com/tuapagina · tiktok.com/@tuoprofilo"
                  className="pl-10 h-12 bg-[#0B1120] border-white/10 focus-visible:ring-cyan-400 focus-visible:ring-offset-0 text-slate-100" data-testid="home-url-input" />
              </div>
              <Button onClick={go} className="h-12 px-6 bg-gradient-to-r from-cyan-400 to-violet-500 hover:from-cyan-300 hover:to-violet-400 text-slate-950 font-semibold rounded-xl" data-testid="home-analyze-button">
                <Sparkles size={16} className="mr-2" /> Analizza gratis la mia attività
              </Button>
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck size={13} className="text-emerald-400" /> Nessuna carta richiesta.</div>
          </div>
        </div>
        <div className="hidden lg:flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 blur-3xl bg-gradient-to-br from-cyan-400/20 to-violet-500/20 rounded-full" />
            <div className="relative nadia-card p-6 w-[340px] space-y-3">
              <div className="flex items-center gap-3"><NadiaAvatar size="sm" thinking /><div className="text-xs text-cyan-300 font-semibold uppercase tracking-wider">Nadia sta lavorando</div></div>
              {[
                [Megaphone, "Campagna Instagram pronta", "PREPARA · aspetta il tuo ok"],
                [MessageCircleMore, "Follow-up WhatsApp a 3 lead", "PREPARA · testo già scritto"],
                [Clapperboard, "Reel 9:16 con le tue foto", "Storyboard incluso"],
              ].map(([Icon, t, s]) => (
                <div key={t} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-300 flex items-center justify-center"><Icon size={14} /></span>
                  <div><div className="text-sm text-slate-100 font-semibold">{t}</div><div className="text-[11px] text-slate-500">{s}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Come funziona */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            ["1", "Incolli il tuo sito", "Nadia legge attività, prodotti, offerte, pubblico, tono e immagini."],
            ["2", "Ricevi la prima analisi gratuita", "Opportunità, obiettivo suggerito, piano, copy, creatività e un possibile Reel."],
            ["3", "Attivi Nadia", "Consiglia, prepara, esegue: tu approvi, lei lavora ogni giorno per te."],
          ].map(([n, t, d]) => (
            <div key={n} className="nadia-card p-5">
              <div className="w-8 h-8 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-cyan-200 text-sm font-bold flex items-center justify-center">{n}</div>
              <div className="mt-3 font-heading text-base md:text-lg font-semibold text-slate-100">{t}</div>
              <div className="text-sm text-slate-400 mt-1">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16" aria-labelledby="home-after-activation-title" data-testid="home-after-activation">
        <div className="nadia-card p-6 sm:p-8">
          <h2 id="home-after-activation-title" className="font-heading text-base md:text-lg font-semibold text-slate-100" data-testid="home-after-activation-title">Cosa fa Nadia dopo che la attivi</h2>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8">
            <ul className="space-y-4 text-sm text-slate-400">
              <li data-testid="home-after-activation-dashboard"><span className="font-semibold text-slate-100">Dashboard:</span> campagne, risultati, lead e chat con Nadia, in un unico spazio.</li>
              <li data-testid="home-after-activation-ad-channels"><span className="font-semibold text-slate-100">Canali ads già previsti:</span> <span className="font-semibold text-blue-400">Meta (Facebook)</span>, <span className="font-semibold text-pink-400">Instagram</span>, <span className="font-semibold"><span className="text-blue-400">G</span><span className="text-red-400">o</span><span className="text-yellow-300">o</span><span className="text-blue-400">g</span><span className="text-green-400">l</span><span className="text-red-400">e</span></span> e <span className="font-semibold text-cyan-300 drop-shadow-[1px_0_0_#f472b6]">TikTok</span>. Da collegare e configurare prima dell’uso.</li>
              <li className="flex flex-wrap items-center gap-2" data-testid="home-after-activation-adsgpt">
                <span className="font-semibold text-slate-100">ChatGPT Ads</span>
                <span className="rounded-full border border-amber-400/20 bg-amber-400/5 px-2.5 py-1 text-xs text-amber-200" data-testid="home-adsgpt-status">Nuova funzione di Nadia</span>
              </li>
              <li data-testid="home-after-activation-whatsapp"><span className="font-semibold text-slate-100">WhatsApp:</span> follow-up sui lead, con WhatsApp collegato e invio approvato.</li>
              <li data-testid="home-after-activation-reel"><span className="font-semibold text-slate-100">Reel e testi:</span> Nadia li prepara dalle foto e dai contenuti rilevati sul tuo sito.</li>
            </ul>
            <div className="border-l-2 border-cyan-400/40 pl-5">
              <div className="flex items-center gap-2 text-cyan-200">
                <Check size={18} aria-hidden="true" />
                <h3 className="text-sm font-semibold" data-testid="home-after-activation-approval-title">Prima la bozza, poi il tuo via libera</h3>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-300" data-testid="home-after-activation-approval-description">Nadia prepara campagna, copy e creatività. In chat, approvare la proposta salva soltanto una bozza. Pubblicazione e spesa richiedono un’autorizzazione separata, sui canali collegati e configurati e previa conferma della piattaforma.</p>
              <p className="mt-5 flex items-start gap-2 text-sm font-medium text-emerald-200" data-testid="home-after-activation-no-spend-without-approval">
                <ShieldCheck size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>Salvare una bozza non pubblica la campagna e non autorizza spese.</span>
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Piani */}
      <section className="max-w-6xl mx-auto px-4 pb-16" id="piani">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300/80">Abbonamenti</div>
        <h2 className="mt-2 font-heading text-base md:text-lg font-semibold text-slate-100" data-testid="home-plans-title">Abbonamenti e percorso su misura. <span className="text-slate-500 font-normal">Scegli come far lavorare Nadia.</span></h2>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="home-plans">
          {(cfg?.plans || []).map((p) => <PlanCard key={p.id} plan={p} onSelect={() => nav(`/piani?plan=${p.id}`)} />)}
          <PlanCard plan={CUSTOM_PLAN} featured />
        </div>
        <div className="mt-4 nadia-card p-5 flex items-start gap-3" data-testid="home-free-message">
          <span className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 flex items-center justify-center shrink-0"><Coins size={16} /></span>
          <div>
            <div className="text-sm font-semibold text-slate-100">{cfg?.free_message}</div>
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {(cfg?.free_features || []).map((f) => (
                <span key={f} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border bg-white/[0.03] border-white/10 text-slate-400"><Check size={9} className="text-emerald-400" />{f}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Informazioni legali nel footer condiviso di App. */}
    </div>
  );
}
